import { useState, useEffect } from 'react';
import type { SiteSettings, SectionKey } from './AdminAppearance';

const M = "'IBM Plex Mono', monospace";

interface Props {
  settings: SiteSettings;
  editingMode: 'light' | 'dark';
  menuItems: string[];
  activeSection: SectionKey | null;
}

export function AppearancePreview({ settings, editingMode, menuItems, activeSection }: Props) {
  const isDark = editingMode === 'dark';
  const bgColor = isDark ? settings.dark_bg_color : '#F7F6F2';
  const textColor = isDark ? settings.dark_text_color : settings.text_color;
  const linkColor = isDark ? settings.dark_link_color : settings.link_color;
  const hoverColor = isDark ? settings.dark_hover_color : settings.hover_color;
  const activeColor = isDark ? settings.dark_active_color : settings.active_color;
  const font = `'${settings.font_family}', sans-serif`;

  // Crossfade key
  const [displaySection, setDisplaySection] = useState<SectionKey | null>(activeSection);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    if (activeSection === displaySection) return;
    setFadeIn(false);
    const t = setTimeout(() => {
      setDisplaySection(activeSection);
      setFadeIn(true);
    }, 200);
    return () => clearTimeout(t);
  }, [activeSection, displaySection]);

  const sectionLabels: Record<SectionKey, string> = {
    tema: 'TEMA', tipografia: 'TIPOGRAFIA', menu: 'MENU', transicoes: 'TRANSIÇÕES', cursor: 'CURSOR',
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      backgroundColor: isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.03)',
      position: 'relative',
    }}>
      {/* Section label */}
      {displaySection && (
        <div style={{
          fontFamily: M, fontSize: 12, fontWeight: 300, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: 'rgba(247,246,242,0.3)',
          padding: '0 0 12px 0', flexShrink: 0,
        }}>
          {sectionLabels[displaySection]}
        </div>
      )}

      {/* Content with crossfade */}
      <div style={{
        flex: 1, overflow: 'hidden',
        opacity: fadeIn ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}>
        {!displaySection && (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.3)' }}>
              Abra uma seção para visualizar
            </span>
          </div>
        )}

        {displaySection === 'tema' && (
          <TemaPreview bgColor={bgColor} textColor={textColor} linkColor={linkColor} hoverColor={hoverColor} font={font} settings={settings} />
        )}

        {displaySection === 'tipografia' && (
          <TypographyPreview settings={settings} font={font} bgColor={bgColor} textColor={textColor} linkColor={linkColor} hoverColor={hoverColor} activeColor={activeColor} />
        )}

        {displaySection === 'menu' && (
          <MenuPreview settings={settings} menuItems={menuItems} bgColor={bgColor} textColor={textColor} linkColor={linkColor} hoverColor={hoverColor} />
        )}

        {displaySection === 'transicoes' && (
          <TransitionPreview settings={settings} bgColor={bgColor} textColor={textColor} />
        )}

        {displaySection === 'cursor' && (
          <CursorPreview settings={settings} />
        )}
      </div>
    </div>
  );
}

/* ── Tema Preview ── */
function TemaPreview({ bgColor, textColor, linkColor, hoverColor, font, settings }: {
  bgColor: string; textColor: string; linkColor: string; hoverColor: string; font: string; settings: SiteSettings;
}) {
  return (
    <div style={{ backgroundColor: bgColor, height: '100%', padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {settings.logo_url && (
        <img src={settings.logo_url} alt="Logo" style={{ maxWidth: settings.logo_size || 80, height: 'auto', objectFit: 'contain' }} />
      )}
      <span style={{ fontFamily: font, fontSize: settings.font_size, fontWeight: settings.font_weight, color: textColor }}>
        Texto de exemplo
      </span>
      <span style={{ fontFamily: font, fontSize: settings.font_size, fontWeight: settings.font_weight, color: linkColor }}>
        Link de exemplo
      </span>
      <span style={{ fontFamily: font, fontSize: settings.font_size, fontWeight: settings.font_weight, color: hoverColor, opacity: 0.8 }}>
        Hover de exemplo
      </span>
    </div>
  );
}

/* ── Typography Preview ── */
function TypographyPreview({ settings, font, bgColor, textColor, linkColor, hoverColor, activeColor }: {
  settings: SiteSettings; font: string; bgColor: string;
  textColor: string; linkColor: string; hoverColor: string; activeColor: string;
}) {
  const [testingHover, setTestingHover] = useState(false);

  const baseTextStyle: React.CSSProperties = {
    fontFamily: font,
    fontSize: settings.font_size,
    fontWeight: settings.font_weight,
    textTransform: settings.text_transform as any,
    textDecoration: settings.text_decoration !== 'none' ? settings.text_decoration : 'none',
  };

  const handleTestHover = () => {
    setTestingHover(true);
    setTimeout(() => setTestingHover(false), 1500);
  };

  return (
    <div style={{ backgroundColor: bgColor, padding: 24, display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      <span style={{ ...baseTextStyle, color: textColor }}>Texto de exemplo</span>
      <span style={{
        ...baseTextStyle, color: linkColor,
        textDecoration: settings.link_underline ? 'underline' : 'none',
        textUnderlineOffset: '4px',
        backgroundColor: settings.link_bg_enabled ? (settings.link_bg_color || 'transparent') : 'transparent',
        padding: settings.link_bg_enabled ? '2px 6px' : 0,
        display: 'inline-block',
      }}>
        Link de exemplo
      </span>
      <span style={{
        ...baseTextStyle,
        color: testingHover ? hoverColor : linkColor,
        textDecoration: testingHover && settings.link_hover_underline ? 'underline' : (settings.link_underline ? 'underline' : 'none'),
        textUnderlineOffset: '4px',
        transition: 'color 0.3s ease',
        opacity: testingHover ? 0.8 : 0.6,
      }}>
        Hover de exemplo
      </span>
      <span style={{ ...baseTextStyle, color: activeColor, opacity: 0.6 }}>Link visitado</span>

      <button
        onClick={handleTestHover}
        style={{
          fontFamily: M, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
          padding: '6px 12px', border: '0.5px solid rgba(247,246,242,0.2)',
          backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)',
          marginTop: 8, alignSelf: 'flex-start', cursor: 'pointer',
        }}
      >
        {testingHover ? 'Testando...' : 'Testar hover'}
      </button>
    </div>
  );
}

/* ── Menu Preview ── */
function MenuPreview({ settings, menuItems, bgColor, textColor, linkColor, hoverColor }: {
  settings: SiteSettings; menuItems: string[];
  bgColor: string; textColor: string; linkColor: string; hoverColor: string;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const items = menuItems.length > 0 ? menuItems : ['Retratos', 'Artes', 'Exposições', 'Galerias', 'Contato'];
  const isVertical = settings.menu_type === 'vertical';

  const getContainerStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'flex',
      flexDirection: isVertical ? 'column' : 'row',
      gap: settings.menu_item_gap ?? 8,
      flexWrap: 'wrap',
      padding: 16,
      backgroundColor: bgColor,
      minHeight: 80,
      height: '100%',
      position: 'relative',
    };

    // Map menu_position to flex justify/align (relative within preview container)
    const ALIGN_MAP: Record<string, { justifyContent: React.CSSProperties['justifyContent']; alignItems: React.CSSProperties['alignItems'] }> = {
      'top-left':      { justifyContent: 'flex-start', alignItems: 'flex-start' },
      'top-center':    { justifyContent: 'center',     alignItems: 'flex-start' },
      'top-right':     { justifyContent: 'flex-end',   alignItems: 'flex-start' },
      'center-left':   { justifyContent: 'flex-start', alignItems: 'center' },
      'center':        { justifyContent: 'center',     alignItems: 'center' },
      'center-right':  { justifyContent: 'flex-end',   alignItems: 'center' },
      'bottom-left':   { justifyContent: 'flex-start', alignItems: 'flex-end' },
      'bottom-center': { justifyContent: 'center',     alignItems: 'flex-end' },
      'bottom-right':  { justifyContent: 'flex-end',   alignItems: 'flex-end' },
    };
    const a = ALIGN_MAP[settings.menu_position] || ALIGN_MAP['top-left'];
    base.justifyContent = a.justifyContent;
    base.alignItems = a.alignItems;

    return base;
  };

  return (
    <div style={{ ...getContainerStyle(), textTransform: settings.text_transform as any }}>
      {settings.menu_bg_enabled && (
        <div style={{
          position: 'absolute', inset: 0,
          backgroundColor: settings.dark_mode_enabled ? settings.dark_menu_bg_color : settings.menu_bg_color,
          borderRadius: settings.menu_border_radius,
          opacity: 0.3,
        }} />
      )}
      {items.map((item, i) => (
        <span
          key={i}
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{
            fontFamily: `'${settings.font_family}', sans-serif`,
            fontSize: Math.min(settings.font_size, 14),
            fontWeight: settings.font_weight,
            textTransform: settings.text_transform as any,
            color: hoveredIdx === i ? hoverColor : linkColor,
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            position: 'relative',
            zIndex: 1,
            padding: settings.link_bg_enabled ? '2px 6px' : 0,
            backgroundColor: settings.link_bg_enabled && hoveredIdx === i
              ? (settings.dark_mode_enabled ? settings.dark_link_bg_color : settings.link_bg_color) : 'transparent',
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

/* ── Transition Preview ── */
function TransitionPreview({ settings, bgColor, textColor }: {
  settings: SiteSettings; bgColor: string; textColor: string;
}) {
  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'exit' | 'enter'>('idle');

  const play = () => {
    if (playing) return;
    setPlaying(true);
    setPhase('exit');
    const dur = settings.page_transition_duration || 400;
    setTimeout(() => {
      setPhase('enter');
      setTimeout(() => { setPhase('idle'); setPlaying(false); }, dur);
    }, dur);
  };

  const getStyle = (): React.CSSProperties => {
    if (phase === 'idle') return { opacity: 1, transform: 'none' };
    const isExit = phase === 'exit';
    const t = settings.page_transition_type;
    switch (t) {
      case 'fade': return { opacity: isExit ? 0 : 1 };
      case 'slide-left': return { opacity: isExit ? 0 : 1, transform: isExit ? 'translateX(-100%)' : 'translateX(0)' };
      case 'slide-right': return { opacity: isExit ? 0 : 1, transform: isExit ? 'translateX(100%)' : 'translateX(0)' };
      case 'slide-up': return { opacity: isExit ? 0 : 1, transform: isExit ? 'translateY(-100%)' : 'translateY(0)' };
      case 'slide-down': return { opacity: isExit ? 0 : 1, transform: isExit ? 'translateY(100%)' : 'translateY(0)' };
      case 'scale': return { opacity: isExit ? 0 : 1, transform: isExit ? 'scale(0.95)' : 'scale(1)' };
      default: return { opacity: 1 };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        width: '100%', flex: 1, backgroundColor: bgColor,
        border: '0.5px solid rgba(247,246,242,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', position: 'relative',
      }}>
        <div style={{
          width: '80%', height: '70%',
          backgroundColor: 'rgba(247,246,242,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transitionProperty: 'opacity, transform',
          transitionDuration: `${settings.page_transition_duration || 400}ms`,
          transitionTimingFunction: settings.page_transition_easing || 'ease',
          ...getStyle(),
        }}>
          <span style={{ fontFamily: M, fontSize: 12, color: textColor, opacity: 0.4, textTransform: 'uppercase' }}>Página</span>
        </div>
      </div>
      <div style={{ height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={play}
          disabled={playing}
          style={{
            fontFamily: M, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.2)',
            backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)',
            cursor: playing ? 'wait' : 'pointer',
            opacity: playing ? 0.4 : 1,
          }}
        >
          {playing ? 'Animando...' : 'TESTAR TRANSIÇÃO'}
        </button>
        <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>
          {settings.page_transition_type === 'none' ? 'Nenhuma' : settings.page_transition_type}
        </span>
      </div>
    </div>
  );
}

/* ── Cursor Preview ── */
function CursorPreview({ settings }: { settings: SiteSettings }) {
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  const getCursorStyle = (): React.CSSProperties => {
    if (!mousePos) return {};
    const base: React.CSSProperties = {
      position: 'absolute',
      left: mousePos.x,
      top: mousePos.y,
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      transition: 'width 0.1s, height 0.1s',
    };

    switch (settings.cursor_type) {
      case 'dot':
        return { ...base, width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(247,246,242,0.85)' };
      case 'ring':
        return { ...base, width: settings.cursor_size || 24, height: settings.cursor_size || 24, borderRadius: '50%', border: '0.5px solid rgba(247,246,242,0.6)' };
      case 'cross':
        return base;
      case 'blue-ring':
        return { ...base, width: settings.cursor_size || 24, height: settings.cursor_size || 24, borderRadius: '50%', border: '2px solid #0000FF' };
      case 'filled-circle':
        return { ...base, width: settings.cursor_size || 16, height: settings.cursor_size || 16, borderRadius: '50%', backgroundColor: 'rgba(247,246,242,0.3)' };
      default:
        return { ...base, width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(247,246,242,0.5)' };
    }
  };

  return (
    <div
      style={{
        height: '100%', backgroundColor: 'rgba(247,246,242,0.03)',
        position: 'relative', cursor: settings.cursor_type === 'none' ? 'none' : 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }}
      onMouseLeave={() => setMousePos(null)}
    >
      {!mousePos && (
        <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>
          Mova o mouse aqui para visualizar
        </span>
      )}
      {mousePos && settings.cursor_type !== 'default' && settings.cursor_type !== 'none' && (
        <>
          {settings.cursor_type === 'cross' ? (
            <svg
              style={{
                position: 'absolute',
                left: mousePos.x - 12,
                top: mousePos.y - 12,
                pointerEvents: 'none',
              }}
              width={24} height={24}
            >
              <line x1={12} y1={0} x2={12} y2={24} stroke="rgba(247,246,242,0.6)" strokeWidth={1} />
              <line x1={0} y1={12} x2={24} y2={12} stroke="rgba(247,246,242,0.6)" strokeWidth={1} />
            </svg>
          ) : settings.cursor_type === 'custom-image' && settings.cursor_image_url ? (
            <img
              src={settings.cursor_image_url}
              alt=""
              style={{
                position: 'absolute',
                left: mousePos.x - (settings.cursor_hotspot_x || 0),
                top: mousePos.y - (settings.cursor_hotspot_y || 0),
                width: settings.cursor_size || 32,
                height: settings.cursor_size || 32,
                pointerEvents: 'none',
                imageRendering: 'pixelated',
              }}
            />
          ) : (
            <div style={getCursorStyle()} />
          )}
        </>
      )}
    </div>
  );
}
